import { FormattedMessage, defineMessages, useIntl } from "react-intl";
import {
  Button,
  makeStyles,
  tokens,
  mergeClasses,
} from "@fluentui/react-components";

import {
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogActions,
  DialogContent,
  DialogBody,
} from "@fluentui/react-dialog";
import {
  ErrorCircleRegular,
  CheckmarkCircleRegular,
} from "@fluentui/react-icons";

import { DIALOG_ACTIONS } from "./messages";

const messages = defineMessages({
  successTitle: {
    defaultMessage: "Connected Update Successful",
    description: "Title of the dialog shown after the wrap-up form was saved.",
  },
  successBody: {
    defaultMessage:
      "Your updates to the Connected Talent Profile and Post Meeting Activity have been saved successfully!",
    description: "Body of the dialog shown after the wrap-up form was saved.",
  },
  errorTitle: {
    defaultMessage: "Connected Update Error",
    description:
      "Title of the dialog shown when saving the wrap-up form failed.",
  },
  errorBody: {
    defaultMessage:
      "We couldn't update Connected due to an error. Please try again. If the issue continues, submit a Service Desk ticket for assistance.",
    description:
      "Body of the dialog shown when saving the wrap-up form failed.",
  },
});

const useStyles = makeStyles({
  title: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  icon: {
    fontSize: "24px",
  },
  successTitle: {
    color: tokens.colorPaletteGreenForeground1,
  },
  errorTitle: {
    color: tokens.colorPaletteRedForeground1,
  },
  errorIcon: {
    color: tokens.colorPaletteRedBorderActive,
  },
  successIcon: {
    color: tokens.colorPaletteGreenBorderActive,
  },
});

export interface SaveResultsProps {
  isError: boolean;
  onCancel: () => void;
}

export default function SaveResultsHandler({
  onCancel,
  isError,
}: SaveResultsProps) {
  const { $t } = useIntl();
  const styles = useStyles();
  return (
    <Dialog open modalType="modal" onOpenChange={onCancel}>
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            className={mergeClasses(
              styles.title,
              isError ? styles.errorTitle : styles.successTitle,
            )}
          >
            {isError ? (
              <ErrorCircleRegular
                className={mergeClasses(styles.icon, styles.errorIcon)}
              />
            ) : (
              <CheckmarkCircleRegular
                className={mergeClasses(styles.icon, styles.successIcon)}
              />
            )}
            {$t(isError ? messages.errorTitle : messages.successTitle)}
          </DialogTitle>
          <DialogContent>
            <p>{$t(isError ? messages.errorBody : messages.successBody)}</p>
          </DialogContent>
          <DialogActions>
            <DialogTrigger>
              <Button appearance="secondary">
                <FormattedMessage {...DIALOG_ACTIONS.close} />
              </Button>
            </DialogTrigger>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
